import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ExamMode from '@/components/ExamMode';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { 
  GraduationCap, 
  LogOut,
  BookOpen,
  Calendar,
  ClipboardList,
  Trophy,
  Clock,
  AlertCircle,
  CheckCircle,
  User,
  School,
  Loader2,
  FileText,
  PenLine,
  Send,
  Save,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Share2,
  Eye,
  Printer,
  FolderOpen,
  Upload,
  Paperclip,
  Download,
  Image,
  Shield,
  Lock,
  AlertTriangle,
  Flag,
  Sparkles
} from 'lucide-react';
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import InteractiveNoteViewer from '@/components/InteractiveNoteViewer';
import QuickCheckModal from '@/components/QuickCheckModal';
import InteractiveExamModal from '@/components/InteractiveExamModal';
import InteractiveExamList from '@/components/InteractiveExamList';
import { StudentResourceLibrary } from '@/components/resource-library/StudentResourceLibrary';
import { StudentActivities } from '@/components/activities/StudentActivities';

const API_URL = '';

// Resilient fetch with retry for Neon DB cold starts
async function resilientFetch(url, options = {}, maxRetries = 3) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000);
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.status >= 500 && attempt < maxRetries) {
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
        continue;
      }
      return response;
    } catch (error) {
      if (attempt < maxRetries && (error.name === 'AbortError' || error.message === 'Failed to fetch')) {
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
        continue;
      }
      throw error;
    }
  }
}

export default function StudentDashboardPage() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [grades, setGrades] = useState(null);
  const [timetable, setTimetable] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [activeTab, setActiveTab] = useState('grades');
  const [loading, setLoading] = useState(true);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [expandedMaterial, setExpandedMaterial] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [materialFilter, setMaterialFilter] = useState('all');
  const [sharedNotes, setSharedNotes] = useState([]);
  const [sharedAssessments, setSharedAssessments] = useState([]);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [takingAssessment, setTakingAssessment] = useState(null);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [examModeAssessment, setExamModeAssessment] = useState(null);
  const [submittingAssessment, setSubmittingAssessment] = useState(false);
  const [iaPrograms, setIAPrograms] = useState([]);
  const [iaLoading, setIALoading] = useState(false);
  const [iaDraftContent, setIADraftContent] = useState({});
  const [iaSubmitting, setIASubmitting] = useState({});
  const [iaFinalContent, setIAFinalContent] = useState({});
  const [expandedIATask, setExpandedIATask] = useState(null);
  const [iaUploading, setIAUploading] = useState({});
  const [iaUploadedFiles, setIAUploadedFiles] = useState({});
  const [enrolledSubjects, setEnrolledSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [viewingNote, setViewingNote] = useState(null);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [newMaterials, setNewMaterials] = useState({});

  useEffect(() => {
    const t = localStorage.getItem('student_token');
    if (!t) return;
    fetch(`${API_URL}/api/resource-library/recent?token=${t}`)
      .then((r) => (r.ok ? r.json() : { groups: [] }))
      .then((d) => setNewMaterials(Object.fromEntries((d.groups || []).map((g) => [`${g.subject.toLowerCase()}|${g.year_level}`, g]))))
      .catch(() => {});
  }, []);
  const newFor = (subj) => newMaterials[`${(subj.subject || '').toLowerCase()}|${parseInt(String(subj.class_name || subj.year || '').match(/\d+/)?.[0] || '0')}`];
  const [quickCheckOpen, setQuickCheckOpen] = useState(false);
  const [quickCheckCount, setQuickCheckCount] = useState(0);
  const [iexState, setIexState] = useState({ loading: true, count: 0 });
  const [examOpen, setExamOpen] = useState(false);
  const [activeExamId, setActiveExamId] = useState(null);

  const SUBJECT_ICONS = {
    'ICT': '💻', 'Economics': '📊', 'Mathematics': '📐', 'English': '📝',
    'Science': '🔬', 'Social Science': '🌍', 'French': '🇫🇷', 'Statistics': '📈', 'Calculus': '∫',
  };
  const SUBJECT_COLORS = {
    'ICT': 'from-blue-500 to-cyan-500', 'Economics': 'from-amber-500 to-orange-500',
    'Mathematics': 'from-purple-500 to-indigo-500', 'English': 'from-emerald-500 to-teal-500',
    'Science': 'from-red-500 to-pink-500', 'Social Science': 'from-yellow-500 to-amber-500',
    'French': 'from-blue-600 to-blue-400', 'Statistics': 'from-violet-500 to-purple-500', 'Calculus': 'from-indigo-500 to-purple-600',
  };

  // Anti-cheat: track paste events per input area
  const pasteTrackerRef = useRef({});
  const handlePaste = (areaId, e) => {
    const pastedText = e.clipboardData?.getData('text') || '';
    if (!pasteTrackerRef.current[areaId]) {
      pasteTrackerRef.current[areaId] = { count: 0, events: [] };
    }
    pasteTrackerRef.current[areaId].count += 1;
    pasteTrackerRef.current[areaId].events.push({
      timestamp: new Date().toISOString(),
      length: pastedText.length,
      preview: pastedText.substring(0, 50)
    });
  };
  const getPasteData = (areaId) => {
    const data = pasteTrackerRef.current[areaId];
    return { paste_count: data?.count || 0, paste_event_count: data?.count || 0, paste_events: data?.events || [] };
  };

  useEffect(() => {
    const studentData = localStorage.getItem('student_user');
    const token = localStorage.getItem('student_token');
    
    if (!studentData || !token) {
      navigate('/student/login');
      return;
    }
    
    setStudent(JSON.parse(studentData));
    loadAllData(token);
  }, [navigate]);

  const loadAllData = async (token) => {
    setLoading(true);
    try {
      await Promise.all([
        loadGrades(token),
        loadTimetable(token),
        loadAssignments(token),
        loadSubjects(token)
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async (token) => {
    try {
      const response = await resilientFetch(`${API_URL}/api/student-portal/my-subjects?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setEnrolledSubjects(data.subjects || []);
      }
    } catch (error) {
      console.error('Error loading subjects:', error);
    }
  };

  const loadGrades = async (token) => {
    try {
      const response = await resilientFetch(`${API_URL}/api/student-portal/grades?token=${token}&year=2026`);
      if (response.ok) {
        const data = await response.json();
        setGrades(data);
      }
    } catch (error) {
      console.error('Error loading grades:', error);
    }
  };

  const loadTimetable = async (token) => {
    try {
      const response = await resilientFetch(`${API_URL}/api/student-portal/timetable?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setTimetable(data);
      }
    } catch (error) {
      console.error('Error loading timetable:', error);
    }
  };

  const loadAssignments = async (token) => {
    try {
      const response = await resilientFetch(`${API_URL}/api/student-portal/assignments?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  };

  const loadMaterials = async (token, subject) => {
    setMaterialsLoading(true);
    try {
      const subjectParam = subject || 'ICT';
      const response = await resilientFetch(`${API_URL}/api/course-materials/student/materials?token=${token}&subject=${encodeURIComponent(subjectParam)}`);
      if (response.ok) {
        const data = await response.json();
        setMaterials(data.materials || []);
        const savedAnswers = {};
        data.materials?.forEach(m => {
          if (m.saved_answer) {
            savedAnswers[m.id] = m.saved_answer;
          }
        });
        setAnswers(savedAnswers);
      }
    } catch (error) {
      console.error('Error loading materials:', error);
    } finally {
      setMaterialsLoading(false);
    }
  };

  const loadSharedNotes = async () => {
    if (!student?.id) return;
    try {
      const response = await resilientFetch(`${API_URL}/api/student-portal/shared-notes/${student.id}`);
      if (response.ok) {
        const data = await response.json();
        setSharedNotes(data);
      }
    } catch (error) {
      console.error('Error loading shared notes:', error);
    }
  };

  const loadSharedAssessments = async () => {
    if (!student?.id) return;
    try {
      const response = await resilientFetch(`${API_URL}/api/student-portal/shared-assessments/${student.id}`);
      if (response.ok) {
        const data = await response.json();
        setSharedAssessments(data);
      }
    } catch (error) {
      console.error('Error loading shared assessments:', error);
    }
  };

  const submitAssessment = async (sharedId) => {
    if (!student?.id) return;
    setSubmittingAssessment(true);
    try {
      const answers = (takingAssessment?.questions || []).map((_, qi) => assessmentAnswers[qi] ?? '');
      const response = await resilientFetch(`${API_URL}/api/student-portal/submit-assessment/${sharedId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: student.id, answers, ...getPasteData(`assessment-${sharedId}`) })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Assessment submitted successfully!');
        setTakingAssessment(null);
        setAssessmentAnswers({});
        loadSharedAssessments();
      } else {
        toast.error(data.detail || 'Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit assessment');
    } finally {
      setSubmittingAssessment(false);
    }
  };

  // Reload content when selected subject changes
  useEffect(() => {
    if (!selectedSubject || !student) return;
    const token = localStorage.getItem('student_token');
    if (token) {
      loadMaterials(token, selectedSubject.subject);
    }
  }, [selectedSubject, student]);

  useEffect(() => {
    if (student && selectedSubject) {
      // Pre-load shared notes and assessments regardless of active tab
      setSharedLoading(true);
      Promise.all([loadSharedNotes(), loadSharedAssessments()]).finally(() => setSharedLoading(false));
    }
  }, [student, selectedSubject]);

  // Check if Quick Check practice sets exist for this subject (any subject, API-driven)
  useEffect(() => {
    setQuickCheckCount(0);
    if (!student || !selectedSubject) return;
    const token = localStorage.getItem('student_token');
    const yr = parseInt(student?.year || student?.stream?.match(/Year\s+(\d+)/)?.[1] || student?.year_level?.replace?.(/\D/g, '') || '0');
    if (!token || !yr) return;
    fetch(`${API_URL}/api/quick-check/chapters?year_level=${yr}&subject=${encodeURIComponent(selectedSubject.subject)}&token=${token}`)
      .then((r) => (r.ok ? r.json() : { chapters: [] }))
      .then((d) => setQuickCheckCount((d.chapters || []).length))
      .catch(() => setQuickCheckCount(0));
  }, [student, selectedSubject]);

  useEffect(() => {
    if (student && selectedSubject && activeTab === 'ia-program') {
      loadIAProgram();
    }
  }, [activeTab, student, selectedSubject]);

  const loadIAProgram = async () => {
    if (!student?.id) return;
    setIALoading(true);
    try {
      const discoverRes = await resilientFetch(`${API_URL}/api/ia-programs/student-programs/${student.id}`);
      if (!discoverRes.ok) { setIAPrograms([]); setIALoading(false); return; }
      const available = await discoverRes.json();
      
      // Filter to only the selected subject
      const filtered = selectedSubject
        ? available.filter(p => p.subject.toLowerCase() === selectedSubject.subject.toLowerCase())
        : available;
      
      if (!filtered.length) { setIAPrograms([]); setIALoading(false); return; }
      
      // Load only the filtered programs for selected subject
      const allPrograms = [];
      const savedDrafts = {};
      for (const prog of filtered) {
        const response = await resilientFetch(`${API_URL}/api/ia-programs/student/${prog.subject}/${prog.year_level}/${prog.year}/${student.id}`);
        if (response.ok) {
          const data = await response.json();
          allPrograms.push(data);
          data.tasks?.forEach(t => t.drafts?.forEach(d => {
            if (d.submission?.content) savedDrafts[d.id] = d.submission.content;
          }));
        }
      }
      setIAPrograms(allPrograms);
      if (allPrograms.length > 0 && allPrograms[0].tasks?.length > 0 && !expandedIATask) {
        setExpandedIATask(allPrograms[0].tasks[0].id);
      }
      setIADraftContent(prev => ({ ...prev, ...savedDrafts }));
    } catch (err) {
      console.error('Error loading IA program:', err);
      setIAPrograms([]);
    } finally {
      setIALoading(false);
    }
  };

  const submitIADraft = async (draftId) => {
    if (!student?.id) return;
    setIASubmitting(prev => ({ ...prev, [draftId]: true }));
    try {
      const response = await resilientFetch(`${API_URL}/api/ia-programs/drafts/${draftId}/submit/${student.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: iaDraftContent[draftId] || '', ...getPasteData(`ia-draft-${draftId}`) })
      });
      if (response.ok) {
        toast.success('Draft submitted successfully!');
        loadIAProgram();
      } else {
        toast.error('Failed to submit draft');
      }
    } catch { toast.error('Failed to submit draft'); }
    finally { setIASubmitting(prev => ({ ...prev, [draftId]: false })); }
  };

  const submitIAFinal = async (taskId) => {
    if (!student?.id) return;
    setIASubmitting(prev => ({ ...prev, [taskId]: true }));
    try {
      const response = await resilientFetch(`${API_URL}/api/ia-programs/tasks/${taskId}/final-submit/${student.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: iaFinalContent[taskId] || '', ...getPasteData(`ia-final-${taskId}`) })
      });
      if (response.ok) {
        toast.success('Final document submitted!');
        loadIAProgram();
      } else {
        toast.error('Failed to submit final');
      }
    } catch { toast.error('Failed to submit final'); }
    finally { setIASubmitting(prev => ({ ...prev, [taskId]: false })); }
  };

  const uploadIAFile = async (entityId, type) => {
    // type is 'draft' or 'final'
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.jpg,.jpeg,.gif,.pdf,.doc,.docx,.txt,.py,.html,.css,.zip';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum 10MB.');
        return;
      }
      setIAUploading(prev => ({ ...prev, [entityId]: true }));
      try {
        const formData = new FormData();
        formData.append('file', file);
        const url = type === 'draft'
          ? `${API_URL}/api/ia-programs/drafts/${entityId}/upload/${student.id}`
          : `${API_URL}/api/ia-programs/tasks/${entityId}/final-upload/${student.id}`;
        const response = await fetch(url, { method: 'POST', body: formData });
        if (response.ok) {
          const data = await response.json();
          toast.success(`File "${file.name}" uploaded!`);
          // Add to local file list
          setIAUploadedFiles(prev => ({
            ...prev,
            [entityId]: [...(prev[entityId] || []), data.file_name]
          }));
          loadIAProgram();
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(err.detail || 'Upload failed');
        }
      } catch { toast.error('Upload failed'); }
      finally { setIAUploading(prev => ({ ...prev, [entityId]: false })); }
    };
    input.click();
  };

  const [autoResults, setAutoResults] = useState({});

  const handleAnswerChange = (materialId, value) => {
    setAnswers(prev => ({ ...prev, [materialId]: value }));
  };

  const handleSaveAnswer = async (materialId, isSubmit = false) => {
    const token = localStorage.getItem('student_token');
    if (!token) return;

    setSubmitting(prev => ({ ...prev, [materialId]: true }));
    try {
      const response = await resilientFetch(`${API_URL}/api/course-materials/student/submit?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          material_id: materialId,
          answer_text: answers[materialId] || '',
          status: isSubmit ? 'submitted' : 'draft',
          ...getPasteData(`material-${materialId}`)
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.auto_correction) {
          setAutoResults(prev => ({ ...prev, [materialId]: data.auto_correction }));
          toast.success(`Submitted! Score: ${data.auto_correction.earned_points}/${data.auto_correction.total_points} (${data.auto_correction.percentage}%)`);
        } else {
          toast.success(isSubmit ? 'Answer submitted successfully!' : 'Draft saved!');
        }
        await loadMaterials(token);
      } else {
        toast.error('Failed to save answer');
      }
    } catch (error) {
      console.error('Error saving answer:', error);
      toast.error('Error saving answer');
    } finally {
      setSubmitting(prev => ({ ...prev, [materialId]: false }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('student_token');
    localStorage.removeItem('student_user');
    toast.success('Logged out successfully');
    navigate('/student/login');
  };

  const getGradeColor = (grade) => {
    switch(grade) {
      case 'A': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'B': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'C': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'D': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'F': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'graded': return <CheckCircle className="h-4 w-4 text-emerald-600" />;
      case 'pending': return <Clock className="h-4 w-4 text-amber-600" />;
      case 'overdue': return <AlertCircle className="h-4 w-4 text-red-600" />;
      default: return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'graded': return <Badge className="bg-emerald-100 text-emerald-800">Graded</Badge>;
      case 'pending': return <Badge className="bg-amber-100 text-amber-800">Pending</Badge>;
      case 'overdue': return <Badge className="bg-red-100 text-red-800">Overdue</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getMaterialTypeIcon = (type) => {
    switch(type) {
      case 'note': return <BookOpen className="h-4 w-4" />;
      case 'exercise': return <PenLine className="h-4 w-4" />;
      case 'assignment': return <FileText className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getMaterialTypeBadge = (type) => {
    switch(type) {
      case 'note': return <Badge className="bg-blue-100 text-blue-800">Notes</Badge>;
      case 'exercise': return <Badge className="bg-purple-100 text-purple-800">Exercise</Badge>;
      case 'assignment': return <Badge className="bg-amber-100 text-amber-800">Assignment</Badge>;
      default: return <Badge variant="outline">{type}</Badge>;
    }
  };

  const filteredMaterials = materials.filter(m => {
    if (materialFilter === 'all') return true;
    return m.material_type === materialFilter;
  });

  // Group materials by unit
  const groupedMaterials = filteredMaterials.reduce((acc, material) => {
    const unit = material.unit || 'General';
    if (!acc[unit]) acc[unit] = [];
    acc[unit].push(material);
    return acc;
  }, {});

  if (!student) return null;

  return (
    <>
      {/* ExamMode overlay - renders on top of everything */}
      {examModeAssessment && (
        <ExamMode
          assessment={examModeAssessment}
          student={student}
          onExit={() => {
            setExamModeAssessment(null);
            Promise.all([loadSharedNotes(), loadSharedAssessments()]);
          }}
        />
      )}
      {/* Interactive Note Viewer for AI Study Notes */}
      {viewingNote && student && (
        <InteractiveNoteViewer
          note={viewingNote}
          studentId={student.id}
          onClose={() => setViewingNote(null)}
        />
      )}
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100" data-testid="student-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-amber-400 to-amber-500 p-3 rounded-xl shadow-md">
                <GraduationCap className="h-8 w-8 text-emerald-900" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Student Portal</h1>
                <p className="text-emerald-100 mt-0.5">Welcome back, {student.name}!</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Student Info Card */}
        <Card className="mb-6 border-0 shadow-md overflow-hidden" data-testid="student-info-card">
          <div className="h-1 bg-gradient-to-r from-emerald-500 via-amber-400 to-emerald-500" />
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <User className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Student ID</p>
                  <p className="font-semibold text-gray-800">{student.birth_registration || student.student_number || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <BookOpen className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Year Level</p>
                  <p className="font-semibold text-gray-800">{student.year_level || (student.stream && student.stream.match(/Year\s+(\d+)/) ? `Year ${student.stream.match(/Year\s+(\d+)/)[1]}` : `Year ${student.year}`) || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Trophy className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Stream</p>
                  <p className="font-semibold text-gray-800">{student.stream || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <School className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">School</p>
                  <p className="font-semibold text-gray-800 text-sm">{student.school_name || '-'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subject Selection or Content View */}
        {!selectedSubject ? (
          /* Subject Cards Grid */
          <div data-testid="subject-cards">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">My Subjects</h2>
            {enrolledSubjects.length === 0 && !loading ? (
              <Card className="border-0 shadow-md">
                <CardContent className="py-12 text-center">
                  <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No subjects enrolled yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {enrolledSubjects.map((subj, idx) => (
                  <Card
                    key={subj.class_id}
                    className="border-0 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
                    onClick={() => { setSelectedSubject(subj); setActiveTab('materials'); }}
                    data-testid={`subject-card-${subj.subject.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className={`h-2 bg-gradient-to-r ${SUBJECT_COLORS[subj.subject] || 'from-gray-400 to-gray-500'}`} />
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="text-3xl">{SUBJECT_ICONS[subj.subject] || '📚'}</div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge variant="outline" className="text-xs">{subj.class_name}</Badge>
                          {newFor(subj) && (
                            <Badge className="bg-amber-400 text-amber-950 hover:bg-amber-400 text-[10px] animate-pulse" data-testid={`subject-new-${subj.subject.toLowerCase().replace(/\s/g, '-')}`}>
                              ✨ New! {newFor(subj).count} file{newFor(subj).count > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <h3 className="font-bold text-gray-800 text-lg group-hover:text-emerald-600 transition-colors">{subj.subject}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        <User className="h-3.5 w-3.5 inline mr-1" />
                        {subj.teacher_name}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Subject Content View */
          <div>
            {/* Back to subjects + subject header */}
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedSubject(null)}
                className="hover:bg-emerald-50"
                data-testid="back-to-subjects-btn"
              >
                <ChevronDown className="h-4 w-4 mr-1 rotate-90" />
                My Subjects
              </Button>
              <div className={`h-6 w-1 rounded-full bg-gradient-to-b ${SUBJECT_COLORS[selectedSubject.subject] || 'from-gray-400 to-gray-500'}`} />
              <h2 className="text-lg font-semibold text-gray-800">
                {SUBJECT_ICONS[selectedSubject.subject] || '📚'} {selectedSubject.subject}
                <span className="text-sm font-normal text-gray-500 ml-2">with {selectedSubject.teacher_name}</span>
              </h2>
            </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 lg:w-auto lg:inline-grid bg-white shadow-sm border">
            <TabsTrigger value="materials" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="materials-tab">
              <BookOpen className="h-4 w-4 mr-2" />
              Course Materials
            </TabsTrigger>
            <TabsTrigger value="study-materials" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="study-materials-tab">
              <FolderOpen className="h-4 w-4 mr-2" />
              Study Materials
            </TabsTrigger>
            <TabsTrigger value="tests-exams" className="data-[state=active]:bg-red-600 data-[state=active]:text-white" data-testid="tests-exams-tab">
              <Shield className="h-4 w-4 mr-2" />
              Tests & Exams
            </TabsTrigger>
            <TabsTrigger value="grades" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="grades-tab">
              <Trophy className="h-4 w-4 mr-2" />
              Grades
            </TabsTrigger>
            {(() => {
              const yearNum = parseInt(student?.year || student?.stream?.match(/Year\s+(\d+)/)?.[1] || student?.year_level?.replace?.(/\D/g, '') || selectedSubject?.year || '0');
              return yearNum >= 12 ? (
                <TabsTrigger value="ia-program" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="ia-program-tab">
                  <ClipboardList className="h-4 w-4 mr-2" />
                  IA Program
                </TabsTrigger>
              ) : null;
            })()}
            <TabsTrigger value="assignments" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="assignments-tab">
              <PenLine className="h-4 w-4 mr-2" />
              Assignments
            </TabsTrigger>
          </TabsList>

          {/* Grades Tab */}
          <TabsContent value="grades">
            <Card className="border-0 shadow-md" data-testid="grades-content">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-amber-50 border-b">
                <CardTitle className="text-emerald-800 flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  Your Grades
                </CardTitle>
                <CardDescription>
                  View your academic performance across all subjects
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                  </div>
                ) : grades && grades.subjects && grades.subjects.length > 0 ? (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-emerald-100 to-amber-100 p-4 rounded-lg mb-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-emerald-700 font-medium">Overall Performance</p>
                          <p className="text-3xl font-bold text-emerald-800">{grades.overall_percentage}%</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Total Subjects</p>
                          <p className="text-2xl font-bold text-gray-800">{grades.total_subjects}</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {grades.subjects.map((subject, index) => (
                        <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`subject-grade-${index}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-800">{subject.subject}</h3>
                              <p className="text-sm text-gray-500">{subject.class_name} - {subject.teacher_name}</p>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-sm text-gray-500">Score</p>
                                <p className="font-semibold">{subject.percentage}%</p>
                              </div>
                              <Badge className={`${getGradeColor(subject.grade)} text-lg px-3 py-1 font-bold`}>
                                {subject.grade}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No grades available</p>
                    <p className="text-sm">Your grades will appear here once they are recorded.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timetable Tab */}
          <TabsContent value="timetable">
            <Card className="border-0 shadow-md" data-testid="timetable-content">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-amber-50 border-b">
                <CardTitle className="text-emerald-800 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Your Timetable
                </CardTitle>
                <CardDescription>
                  {timetable?.class_name ? `Class: ${timetable.class_name}` : 'View your weekly class schedule'}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                  </div>
                ) : timetable && timetable.timetable ? (
                  <div className="overflow-x-auto">
                    {(() => {
                      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
                      const periodMap = {};
                      const tt = timetable.timetable;
                      if (tt.Monday || tt.Tuesday || tt.Wednesday || tt.Thursday || tt.Friday) {
                        days.forEach(day => {
                          const dayData = tt[day] || [];
                          dayData.forEach(slot => {
                            const periodKey = slot.period || slot.time || `Period ${slot.period_number || '?'}`;
                            if (!periodMap[periodKey]) periodMap[periodKey] = { time: slot.time };
                            periodMap[periodKey][day] = {
                              subject: slot.subject || slot.class_name || '-',
                              room: slot.room || ''
                            };
                          });
                        });
                      } else {
                        Object.assign(periodMap, tt);
                      }
                      const periods = Object.keys(periodMap).sort((a, b) => {
                        const numA = parseInt(a.replace(/\D/g, '')) || 0;
                        const numB = parseInt(b.replace(/\D/g, '')) || 0;
                        return numA - numB;
                      });
                      const renderCell = (data) => {
                        if (!data || data === '-') return '-';
                        if (typeof data === 'string') return data;
                        const subject = data.subject || '-';
                        const room = data.room;
                        if (room && room !== 'TBD' && room !== subject) {
                          return (
                            <div>
                              <div className="font-medium">{subject}</div>
                              <div className="text-xs text-emerald-600">{room}</div>
                            </div>
                          );
                        }
                        return subject;
                      };
                      return (
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-emerald-50">
                              <th className="border p-2 text-left font-medium text-emerald-800">Period</th>
                              {days.map(day => (
                                <th key={day} className="border p-2 text-center font-medium text-emerald-800">{day}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {periods.map((period, index) => {
                              const periodData = periodMap[period] || {};
                              return (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="border p-2 font-medium bg-gray-50">{period}</td>
                                  {days.map(day => (
                                    <td key={day} className="border p-2 text-center text-sm">{renderCell(periodData[day])}</td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No timetable available</p>
                    <p className="text-sm">{timetable?.message || 'Your timetable will appear here once it is set up.'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Course Materials Tab */}
          <TabsContent value="materials">
            <Card className="border-0 shadow-md" data-testid="materials-content">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-amber-50 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-emerald-800 flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      {selectedSubject?.subject || 'ICT'} Course Materials
                    </CardTitle>
                    <CardDescription>
                      Study notes, exercises, and assignments for Year {student?.year_level || student?.stream?.match(/Year\s+(\d+)/)?.[1] || ''} {selectedSubject?.subject || 'ICT'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={materialFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMaterialFilter('all')}
                      className={materialFilter === 'all' ? 'bg-emerald-600' : ''}
                      data-testid="filter-all"
                    >
                      All
                    </Button>
                    <Button
                      variant={materialFilter === 'note' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMaterialFilter('note')}
                      className={materialFilter === 'note' ? 'bg-blue-600' : ''}
                      data-testid="filter-notes"
                    >
                      Notes
                    </Button>
                    <Button
                      variant={materialFilter === 'exercise' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMaterialFilter('exercise')}
                      className={materialFilter === 'exercise' ? 'bg-purple-600' : ''}
                      data-testid="filter-exercises"
                    >
                      Exercises
                    </Button>
                    <Button
                      variant={materialFilter === 'assignment' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMaterialFilter('assignment')}
                      className={materialFilter === 'assignment' ? 'bg-amber-600' : ''}
                      data-testid="filter-assignments"
                    >
                      Assignments
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <StudentResourceLibrary
                  subject={selectedSubject?.subject || 'ICT'}
                  yearLevel={parseInt(student?.year || student?.stream?.match(/Year\s+(\d+)/)?.[1] || String(student?.year_level || '').replace(/\D/g, '') || selectedSubject?.year || '0')}
                  token={localStorage.getItem('student_token')}
                />
                {materialsLoading || loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                  </div>
                ) : Object.keys(groupedMaterials).length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(groupedMaterials).map(([unit, unitMaterials]) => (
                      <div key={unit} className="border rounded-lg overflow-hidden">
                        <div className="bg-emerald-50 px-4 py-3 border-b">
                          <h3 className="font-semibold text-emerald-800">{unit}</h3>
                        </div>
                        <div className="divide-y">
                          {unitMaterials.map((material) => (
                            <div key={material.id} className="bg-white" data-testid={`material-${material.id}`}>
                              <div 
                                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
                                onClick={() => setExpandedMaterial(expandedMaterial === material.id ? null : material.id)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded-lg ${
                                    material.material_type === 'note' ? 'bg-blue-100' :
                                    material.material_type === 'exercise' ? 'bg-purple-100' : 'bg-amber-100'
                                  }`}>
                                    {getMaterialTypeIcon(material.material_type)}
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-gray-800">{material.title}</h4>
                                    <div className="flex items-center gap-2 mt-1">
                                      {getMaterialTypeBadge(material.material_type)}
                                      {material.submission_status && (
                                        <Badge className={
                                          material.submission_status === 'submitted' 
                                            ? 'bg-emerald-100 text-emerald-800' 
                                            : 'bg-gray-100 text-gray-800'
                                        }>
                                          {material.submission_status === 'submitted' ? 'Submitted' : 'Draft Saved'}
                                        </Badge>
                                      )}
                                      {material.teacher_feedback && (
                                        <Badge className="bg-blue-100 text-blue-800">
                                          <MessageSquare className="h-3 w-3 mr-1" />
                                          Feedback
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {expandedMaterial === material.id ? (
                                  <ChevronUp className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                              
                              {expandedMaterial === material.id && (
                                <div className="px-4 pb-4 bg-gray-50 border-t">
                                  {/* Material Content */}
                                  <div className="mt-4 p-4 bg-white rounded-lg border">
                                    {material.content && material.content.trim().startsWith('<') ? (
                                      <div
                                        className="prose prose-sm max-w-none text-gray-700"
                                        dangerouslySetInnerHTML={{ __html: material.content }}
                                      />
                                    ) : (
                                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                                        {material.content}
                                      </pre>
                                    )}
                                  </div>

                                  {/* Quick Check — Test Yourself button (only on textbook/study guide content for ICT) */}
                                  {selectedSubject?.subject === 'ICT' && material.material_type === 'note' && /textbook|study guide|engagement/i.test(material.title || '') && (
                                    <div className="mt-3">
                                      <Button
                                        onClick={() => setQuickCheckOpen(true)}
                                        className="w-full bg-gradient-to-r from-[#0F2A4A] to-[#0E7C7B] hover:from-[#0a1f3a] hover:to-[#0a5f5e] text-white shadow-md"
                                        data-testid={`open-quick-check-btn-${material.id}`}
                                      >
                                        <Sparkles className="h-4 w-4 mr-2 text-yellow-300" />
                                        Test Yourself — Quick Check
                                      </Button>
                                      <p className="text-xs text-center text-gray-500 mt-1">
                                        Type your answer, reveal the correct one, and earn badges per chapter.
                                      </p>
                                    </div>
                                  )}
                                  
                                  {/* Answer Section for Exercises and Assignments */}
                                  {(material.material_type === 'exercise' || material.material_type === 'assignment') && (
                                    <div className="mt-4">
                                      <div className="flex items-center justify-between mb-2">
                                        <label className="font-medium text-gray-700">
                                          Your Answer:
                                        </label>
                                        {material.score !== null && material.score !== undefined && (
                                          <Badge className="bg-emerald-100 text-emerald-800 text-sm px-3 py-1" data-testid={`score-badge-${material.id}`}>
                                            Score: {material.score}
                                          </Badge>
                                        )}
                                      </div>
                                      
                                      <Textarea
                                        placeholder="Type your answer here..."
                                        value={answers[material.id] || ''}
                                        onChange={(e) => handleAnswerChange(material.id, e.target.value)}
                                        onPaste={(e) => handlePaste(`material-${material.id}`, e)}
                                        className="min-h-[200px] mb-3"
                                        disabled={material.submission_status === 'submitted'}
                                        data-testid={`answer-input-${material.id}`}
                                      />
                                      
                                      {/* Auto-Correction Results */}
                                      {(autoResults[material.id] || (material.submission_status === 'submitted' && material.teacher_feedback)) && (
                                        <div className="mb-3 rounded-lg border overflow-hidden" data-testid={`feedback-section-${material.id}`}>
                                          {/* Score Header */}
                                          {(() => {
                                            const result = autoResults[material.id];
                                            const pct = result ? result.percentage : null;
                                            const headerColor = pct !== null 
                                              ? (pct >= 70 ? 'bg-emerald-600' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500')
                                              : 'bg-blue-600';
                                            return (
                                              <div className={`${headerColor} text-white px-4 py-3 flex items-center justify-between`}>
                                                <span className="font-semibold flex items-center gap-2">
                                                  <CheckCircle className="h-4 w-4" />
                                                  {result ? 'Auto-Correction Results' : 'Teacher Feedback'}
                                                </span>
                                                {result && (
                                                  <span className="text-lg font-bold">
                                                    {result.earned_points}/{result.total_points} ({result.percentage}%)
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })()}
                                          
                                          {/* Question-by-question results */}
                                          {autoResults[material.id]?.results && (
                                            <div className="divide-y">
                                              {autoResults[material.id].results.map((r, idx) => (
                                                <div key={idx} className={`px-4 py-2 text-sm flex items-center justify-between ${
                                                  r.status === 'correct' ? 'bg-emerald-50' : 
                                                  r.status === 'partial' ? 'bg-amber-50' : 'bg-red-50'
                                                }`}>
                                                  <div className="flex items-center gap-2">
                                                    {r.status === 'correct' ? (
                                                      <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                                    ) : r.status === 'partial' ? (
                                                      <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                                    ) : (
                                                      <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                                    )}
                                                    <span className="font-medium">Q{r.question_number}:</span>
                                                    <span className="text-gray-600 truncate max-w-[300px]">{r.question_text}</span>
                                                  </div>
                                                  <Badge className={
                                                    r.status === 'correct' ? 'bg-emerald-100 text-emerald-800' :
                                                    r.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                                                    'bg-red-100 text-red-800'
                                                  }>
                                                    {r.points_earned}/{r.points_possible}
                                                  </Badge>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          
                                          {/* Text feedback from teacher or auto */}
                                          {material.teacher_feedback && !autoResults[material.id] && (
                                            <div className="p-4 bg-blue-50">
                                              <p className="text-sm font-medium text-blue-800 mb-1">
                                                <MessageSquare className="h-4 w-4 inline mr-1" />
                                                Feedback:
                                              </p>
                                              <pre className="text-sm text-blue-700 whitespace-pre-wrap font-sans">{material.teacher_feedback}</pre>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {material.submission_status !== 'submitted' && (
                                        <div className="flex gap-2">
                                          <Button
                                            variant="outline"
                                            onClick={() => handleSaveAnswer(material.id, false)}
                                            disabled={submitting[material.id]}
                                            data-testid={`save-draft-${material.id}`}
                                          >
                                            {submitting[material.id] ? (
                                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                              <Save className="h-4 w-4 mr-2" />
                                            )}
                                            Save Draft
                                          </Button>
                                          <Button
                                            onClick={() => handleSaveAnswer(material.id, true)}
                                            disabled={submitting[material.id] || !answers[material.id]}
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                            data-testid={`submit-answer-${material.id}`}
                                          >
                                            {submitting[material.id] ? (
                                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                              <Send className="h-4 w-4 mr-2" />
                                            )}
                                            Submit Answer
                                          </Button>
                                        </div>
                                      )}
                                      
                                      {material.submission_status === 'submitted' && !autoResults[material.id] && !material.teacher_feedback && (
                                        <p className="text-sm text-emerald-600 flex items-center gap-1">
                                          <CheckCircle className="h-4 w-4" />
                                          Your answer has been submitted - awaiting auto-correction
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No course materials available</p>
                    <p className="text-sm">Course materials will appear here once they are uploaded by your teacher.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Study Materials Tab - Notes & Assessments grouped by Subject */}
          <TabsContent value="study-materials">
            <Card className="border-0 shadow-md" data-testid="study-materials-content">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                <CardTitle className="text-blue-800 flex items-center gap-2">
                  <FolderOpen className="h-5 w-5" />
                  Study Materials
                </CardTitle>
                <CardDescription>Notes and assessments shared by your teachers, organized by subject</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                {/* Quick Check practice banner (any subject with quick check sets) */}
                {quickCheckCount > 0 && !takingAssessment && (
                  <div className="mb-6 border-2 border-sky-300 rounded-xl bg-gradient-to-br from-sky-50 to-indigo-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="quick-check-banner-materials">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-sky-600" />
                        Test Yourself — Quick Check
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {quickCheckCount} practice sets for {selectedSubject?.subject}. Answer, reveal the correct answer, self-rate and earn badges.
                      </p>
                    </div>
                    <Button
                      onClick={() => setQuickCheckOpen(true)}
                      className="bg-gradient-to-r from-[#0F2A4A] to-[#0E7C7B] hover:from-[#0a1f3a] hover:to-[#0a5f5e] text-white shadow-md"
                      data-testid="open-quick-check-materials-btn"
                    >
                      <Sparkles className="h-4 w-4 mr-2 text-yellow-300" />
                      Practise Now
                    </Button>
                  </div>
                )}
                {sharedLoading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
                ) : takingAssessment ? (
                  /* Assessment Taking View */
                  <div data-testid="assessment-taking-view">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">{takingAssessment.title}</h3>
                      <Button variant="outline" size="sm" onClick={() => { setTakingAssessment(null); setAssessmentAnswers({}); }}>Cancel</Button>
                    </div>
                    {takingAssessment.instructions && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm">{takingAssessment.instructions}</div>
                    )}
                    <div className="space-y-4">
                      {(takingAssessment.questions || []).map((q, qi) => (
                        <div key={qi} className="border rounded-lg p-4 bg-gray-50" data-testid={`question-${qi}`}>
                          <p className="font-medium mb-2">
                            <span className="text-emerald-600">Q{qi + 1}.</span> {q.question || q.text || ''}
                            {q.marks && <span className="text-xs text-muted-foreground ml-2">({q.marks} marks)</span>}
                            <Badge variant="outline" className="text-xs capitalize ml-2">{(q.type || '').replace('_', ' ')}</Badge>
                          </p>
                          {q.type === 'matching' && q.column_a ? (
                            <div className="ml-4 space-y-3">
                              <div className="grid grid-cols-2 gap-4 bg-white p-3 rounded border">
                                <div>
                                  <strong className="text-sm text-blue-700">Column A</strong>
                                  {q.column_a.map((item, j) => <div key={j} className="text-sm mt-1">{j + 1}. {item}</div>)}
                                </div>
                                <div>
                                  <strong className="text-sm text-green-700">Column B</strong>
                                  {q.column_b.map((item, j) => <div key={j} className="text-sm mt-1">{String.fromCharCode(65 + j)}. {item}</div>)}
                                </div>
                              </div>
                              <div className="space-y-2">
                                {q.column_a.map((item, j) => (
                                  <div key={j} className="flex items-center gap-2 text-sm">
                                    <span className="font-medium min-w-[20px]">{j + 1}.</span>
                                    <select
                                      className="border rounded px-2 py-1 text-sm bg-white"
                                      value={(assessmentAnswers[qi] || {})[j] || ''}
                                      onChange={(e) => {
                                        const current = assessmentAnswers[qi] || {};
                                        setAssessmentAnswers(prev => ({ ...prev, [qi]: { ...current, [j]: e.target.value } }));
                                      }}
                                      data-testid={`match-${qi}-${j}`}
                                    >
                                      <option value="">Select match...</option>
                                      {q.column_b.map((b, bi) => (
                                        <option key={bi} value={String.fromCharCode(65 + bi)}>{String.fromCharCode(65 + bi)}. {b}</option>
                                      ))}
                                    </select>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : q.options && q.options.length > 0 ? (
                            <div className="space-y-2 ml-4">
                              {q.options.map((opt, oi) => (
                                <label key={oi} className="flex items-center gap-2 cursor-pointer">
                                  <input 
                                    type="radio" 
                                    name={`q-${qi}`} 
                                    value={opt}
                                    checked={assessmentAnswers[qi] === opt}
                                    onChange={() => setAssessmentAnswers(prev => ({ ...prev, [qi]: opt }))}
                                    className="text-emerald-600"
                                    data-testid={`option-${qi}-${oi}`}
                                  />
                                  <span className="text-sm">{opt}</span>
                                </label>
                              ))}
                            </div>
                          ) : (q.type === 'fill_blank' || q.type === 'fill_in_blank') ? (
                            <Input
                              placeholder="Fill in the blank..."
                              value={assessmentAnswers[qi] || ''}
                              onChange={(e) => setAssessmentAnswers(prev => ({ ...prev, [qi]: e.target.value }))}
                              onPaste={(e) => handlePaste(`assessment-${takingAssessment?.id}`, e)}
                              className="mt-2 max-w-md"
                              data-testid={`fill-blank-${qi}`}
                            />
                          ) : (
                            <Textarea 
                              placeholder="Write your answer here..."
                              value={assessmentAnswers[qi] || ''}
                              onChange={(e) => setAssessmentAnswers(prev => ({ ...prev, [qi]: e.target.value }))}
                              onPaste={(e) => handlePaste(`assessment-${takingAssessment?.id}`, e)}
                              className="mt-2"
                              data-testid={`answer-input-${qi}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                      <Button 
                        variant="outline"
                        disabled={submittingAssessment}
                        data-testid="save-draft-assessment-btn"
                        onClick={() => {
                          const draftKey = `assessment_draft_${takingAssessment.id}`;
                          localStorage.setItem(draftKey, JSON.stringify(assessmentAnswers));
                          toast.success('Draft saved! Your answers are saved locally.');
                        }}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save Draft
                      </Button>
                      <Button 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        disabled={submittingAssessment}
                        data-testid="submit-assessment-btn"
                        onClick={() => submitAssessment(takingAssessment.id)}
                      >
                        {submittingAssessment ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        Submit Assessment
                      </Button>
                    </div>
                  </div>
                ) : (() => {
                  // Filter by selected subject - EXCLUDE lockdown assessments
                  const subjectFilter = selectedSubject?.subject?.toLowerCase();
                  const filteredNotes = sharedNotes.filter(n => !subjectFilter || (n.subject || '').toLowerCase() === subjectFilter);
                  const filteredAssessments = sharedAssessments.filter(a => !a.lockdown_mode && (!subjectFilter || (a.subject || '').toLowerCase() === subjectFilter));
                  const subjectMap = {};
                  filteredNotes.forEach(note => {
                    const subj = note.subject || 'General';
                    if (!subjectMap[subj]) subjectMap[subj] = { notes: [], assessments: [] };
                    subjectMap[subj].notes.push(note);
                  });
                  filteredAssessments.forEach(assessment => {
                    const subj = assessment.subject || 'General';
                    if (!subjectMap[subj]) subjectMap[subj] = { notes: [], assessments: [] };
                    subjectMap[subj].assessments.push(assessment);
                  });
                  const subjects = Object.keys(subjectMap).sort();

                  if (subjects.length === 0) {
                    return (
                      <div className="text-center py-12 text-gray-500">
                        <FolderOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium mb-2">No study materials yet</p>
                        <p className="text-sm">Your teachers will share notes and assessments here.</p>
                      </div>
                    );
                  }

                  return (
                    <Accordion type="multiple" defaultValue={subjects} className="w-full" data-testid="study-materials-accordion">
                      {subjects.map(subject => (
                        <AccordionItem key={subject} value={subject} className="border rounded-lg mb-3 overflow-hidden">
                          <AccordionTrigger className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-blue-50 hover:from-emerald-100 hover:to-blue-100 hover:no-underline" data-testid={`subject-accordion-${subject}`}>
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-emerald-100 rounded-lg">
                                <BookOpen className="h-4 w-4 text-emerald-700" />
                              </div>
                              <div className="text-left">
                                <span className="font-semibold text-emerald-800">{subject}</span>
                                <div className="flex gap-2 mt-0.5">
                                  {subjectMap[subject].notes.length > 0 && (
                                    <span className="text-xs text-blue-600">{subjectMap[subject].notes.length} note{subjectMap[subject].notes.length !== 1 ? 's' : ''}</span>
                                  )}
                                  {subjectMap[subject].assessments.length > 0 && (
                                    <span className="text-xs text-emerald-600">{subjectMap[subject].assessments.length} assessment{subjectMap[subject].assessments.length !== 1 ? 's' : ''}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            {/* Notes Section */}
                            {subjectMap[subject].notes.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-blue-700 flex items-center gap-2 mb-2 mt-2">
                                  <FileText className="h-4 w-4" />
                                  Notes
                                </h4>
                                <div className="grid gap-3 md:grid-cols-2">
                                  {subjectMap[subject].notes.map(note => (
                                    <div 
                                      key={note.id} 
                                      className="border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer bg-white"
                                      data-testid={`shared-note-${note.id}`}
                                      onClick={async () => {
                                        // Lazy load full content
                                        try {
                                          const contentRes = await fetch(`${API_URL}/api/student-portal/note-content/${note.id}`);
                                          if (!contentRes.ok) throw new Error('Failed to load');
                                          const contentData = await contentRes.json();
                                          
                                          if (contentData.type === 'ai_study_note') {
                                            setViewingNote({...note, content: contentData.content, images: contentData.images, type: 'ai_study_note'});
                                            return;
                                          }
                                          // Original lesson-based notes - open print window
                                          let notesData = null;
                                          if (contentData.content) {
                                            try { notesData = JSON.parse(contentData.content); } catch(e) { notesData = null; }
                                          }
                                        const printWindow = window.open('', '_blank');
                                        if (notesData && notesData.sections) {
                                          printWindow.document.write(`
                                            <!DOCTYPE html><html><head><title>${notesData.title || note.title}</title>
                                            <style>
                                              body { font-family: 'Segoe UI', Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px 30px; color: #1a1a1a; line-height: 1.7; }
                                              h1 { text-align: center; color: #fff; background: linear-gradient(135deg, #1e3a5f, #1e40af); padding: 18px 24px; border-radius: 12px; font-size: 21px; margin-bottom: 28px; }
                                              h2 { color: #1e3a5f; border-left: 4px solid #3b82f6; padding-left: 14px; margin-top: 32px; font-size: 17px; }
                                              .section { margin-bottom: 28px; }
                                              .content { margin: 10px 0; text-align: justify; font-size: 14px; color: #334155; }
                                              .key-points { background: linear-gradient(135deg, #eff6ff, #dbeafe); border-radius: 10px; padding: 14px 18px; margin: 14px 0; border-left: 4px solid #3b82f6; }
                                              .key-points h3 { color: #1e40af; font-size: 13px; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.5px; }
                                              .key-points ul { margin: 0; padding-left: 18px; }
                                              .key-points li { margin: 5px 0; font-size: 13px; color: #1e3a5f; }
                                              .examples { background: linear-gradient(135deg, #f0fdf4, #dcfce7); border-radius: 10px; padding: 14px 18px; margin: 14px 0; border-left: 4px solid #22c55e; }
                                              .examples h3 { color: #166534; font-size: 13px; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.5px; }
                                              .diagram { background: #0f172a; border-radius: 12px; padding: 18px 20px; margin: 18px 0; }
                                              .diagram h3 { color: #38bdf8; font-size: 13px; margin: 0 0 10px 0; text-transform: uppercase; }
                                              .diagram pre { font-family: 'Courier New', monospace; font-size: 11.5px; white-space: pre; background: #1e293b; color: #e2e8f0; padding: 16px; border-radius: 8px; }
                                              .vocab-table { width: 100%; border-collapse: collapse; margin: 16px 0; }
                                              .vocab-table th { background: #1e3a5f; color: white; padding: 10px 14px; text-align: left; font-size: 13px; }
                                              .vocab-table td { padding: 10px 14px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
                                              .summary { background: #fef3c7; border-radius: 10px; padding: 16px 18px; margin: 24px 0; border-left: 4px solid #f59e0b; }
                                              .summary h3 { color: #92400e; margin: 0 0 8px 0; font-size: 13px; text-transform: uppercase; }
                                              .review { margin-top: 28px; background: #faf5ff; border-radius: 10px; padding: 16px 18px; border-left: 4px solid #8b5cf6; }
                                              .review h2 { border: none; padding-left: 0; margin-top: 0; color: #6d28d9; }
                                              .review ol { padding-left: 20px; }
                                              .review li { margin: 8px 0; font-size: 14px; color: #4c1d95; }
                                              .btn { background: #1e40af; color: white; padding: 10px 20px; border: none; border-radius: 8px; cursor: pointer; margin-right: 10px; }
                                              .btn-close { background: #e2e8f0; color: #1e293b; }
                                              .footer { text-align: center; color: #94a3b8; font-size: 11px; margin-top: 36px; border-top: 2px solid #e2e8f0; padding-top: 12px; }
                                              @media print { .no-print { display: none !important; } }
                                            </style></head><body>
                                            <div class="no-print" style="margin-bottom: 20px;">
                                              <button class="btn" onclick="window.print()">Print</button>
                                              <button class="btn btn-close" onclick="window.close()">Close</button>
                                            </div>
                                            <h1>${notesData.title || note.title}</h1>
                                            ${(notesData.sections || []).map(s => '<div class="section"><h2>' + (s.heading || '') + '</h2><div class="content">' + (s.content || '') + '</div>' + (s.key_points && s.key_points.length > 0 ? '<div class="key-points"><h3>Key Points</h3><ul>' + s.key_points.map(p => '<li>' + p + '</li>').join('') + '</ul></div>' : '') + (s.examples && s.examples.length > 0 ? '<div class="examples"><h3>Examples</h3><ul>' + s.examples.map(e => '<li>' + e + '</li>').join('') + '</ul></div>' : '') + (s.diagram ? '<div class="diagram"><h3>' + (s.diagram.title || '') + '</h3><pre>' + (s.diagram.description || '') + '</pre></div>' : '') + '</div>').join('')}
                                            ${notesData.vocabulary && notesData.vocabulary.length > 0 ? '<h2>Vocabulary</h2><table class="vocab-table"><tr><th>Term</th><th>Definition</th></tr>' + notesData.vocabulary.map(v => '<tr><td><strong>' + (v.term || '') + '</strong></td><td>' + (v.definition || '') + '</td></tr>').join('') + '</table>' : ''}
                                            ${notesData.summary ? '<div class="summary"><h3>Summary</h3><p>' + notesData.summary + '</p></div>' : ''}
                                            ${notesData.review_questions && notesData.review_questions.length > 0 ? '<div class="review"><h2>Review Questions</h2><ol>' + notesData.review_questions.map(q => '<li>' + q + '</li>').join('') + '</ol></div>' : ''}
                                            <div class="footer">Shared by ${note.teacher_name} | ${note.shared_at ? new Date(note.shared_at).toLocaleDateString() : ''}</div>
                                            </body></html>
                                          `);
                                        } else {
                                          printWindow.document.write(`<html><body><h1>${note.title}</h1><p>${contentData.content || 'No content available'}</p></body></html>`);
                                        }
                                        printWindow.document.close();
                                        } catch (err) {
                                          console.error('Error loading note:', err);
                                          toast.error('Failed to load note content. Please try again.');
                                        }
                                      }}
                                    >
                                      <h4 className="font-semibold text-blue-700 text-sm">
                                        {note.type === 'ai_study_note' && <span className="text-emerald-600 mr-1">AI</span>}
                                        {note.title}
                                      </h4>
                                      <div className="flex gap-2 mt-1.5 flex-wrap">
                                        <Badge variant="outline" className="text-xs">Year {note.year_level}</Badge>
                                        {note.type === 'ai_study_note' ? (
                                          <Badge className="text-xs bg-emerald-100 text-emerald-700">AI Illustrated Notes</Badge>
                                        ) : (
                                          <Badge className="text-xs bg-blue-100 text-blue-700">Notes</Badge>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1.5">By {note.teacher_name} {note.shared_at ? `| ${new Date(note.shared_at).toLocaleDateString()}` : ''}</p>
                                      <p className="text-xs mt-1">
                                        {note.type === 'ai_study_note' ? (
                                          <span className="text-emerald-600 font-medium">Click to study — complete practice questions</span>
                                        ) : (
                                          <span className="text-blue-500">Click to view & print</span>
                                        )}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Assessments Section */}
                            {subjectMap[subject].assessments.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-emerald-700 flex items-center gap-2 mb-2 mt-2">
                                  <ClipboardList className="h-4 w-4" />
                                  Assessments
                                </h4>
                                <div className="space-y-3">
                                  {subjectMap[subject].assessments.map(assessment => (
                                    <div key={assessment.id} className="border rounded-lg p-3 bg-white" data-testid={`shared-assessment-${assessment.id}`}>
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <h4 className="font-semibold text-emerald-700 text-sm">{assessment.title}</h4>
                                          <div className="flex gap-2 mt-1.5 flex-wrap">
                                            <Badge variant="outline" className="text-xs">Year {assessment.year_level}</Badge>
                                            <Badge variant="outline" className="text-xs capitalize">{(assessment.assessment_type || '').replace('_', ' ')}</Badge>
                                            <Badge variant="outline" className="text-xs">{assessment.num_questions} Questions</Badge>
                                            {assessment.total_marks && <Badge variant="outline" className="text-xs">{assessment.total_marks} Marks</Badge>}
                                          </div>
                                          <p className="text-xs text-muted-foreground mt-1.5">By {assessment.teacher_name} | {assessment.shared_at ? new Date(assessment.shared_at).toLocaleDateString() : ''}</p>
                                        </div>
                                        <div>
                                          {assessment.submission ? (
                                            assessment.submission.status === 'graded' ? (
                                              <div className="text-right">
                                                <Badge className="bg-green-100 text-green-700 text-sm">{assessment.submission.marks}/{assessment.submission.total_marks || '?'}</Badge>
                                                <p className="text-xs text-green-600 mt-1">Graded</p>
                                              </div>
                                            ) : (
                                              <Badge className="bg-yellow-100 text-yellow-700">Submitted</Badge>
                                            )
                                          ) : (
                                            <Button 
                                              size="sm" 
                                              className="bg-emerald-600 hover:bg-emerald-700"
                                              data-testid={`take-assessment-${assessment.id}`}
                                              onClick={() => {
                                                // Use ExamMode for lockdown tests or timed tests
                                                if (assessment.lockdown_mode) {
                                                  setExamModeAssessment(assessment);
                                                } else {
                                                  setTakingAssessment(assessment);
                                                  const draftKey = `assessment_draft_${assessment.id}`;
                                                  const draft = localStorage.getItem(draftKey);
                                                  if (draft) {
                                                    try {
                                                      setAssessmentAnswers(JSON.parse(draft));
                                                      toast.info('Draft loaded - your previous answers are restored.');
                                                    } catch(e) { setAssessmentAnswers({}); }
                                                  } else {
                                                    setAssessmentAnswers({});
                                                  }
                                                }
                                              }}
                                            >
                                              <PenLine className="h-4 w-4 mr-1" /> Take Assessment
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                      {assessment.submission?.status === 'graded' && assessment.submission.feedback && (
                                        <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-2">
                                          <p className="text-xs font-medium text-green-800">Teacher Feedback:</p>
                                          <p className="text-xs text-green-700 mt-0.5">{assessment.submission.feedback}</p>
                                        </div>
                                      )}
                                      {/* Answer Review for non-lockdown assessments */}
                                      {assessment.submission?.status === 'graded' && assessment.questions && (
                                        <div className="mt-2 border rounded-lg overflow-hidden" data-testid={`answer-review-${assessment.id}`}>
                                          <div className="bg-indigo-600 text-white px-3 py-1.5 flex items-center justify-between">
                                            <span className="text-xs font-semibold flex items-center gap-1.5">
                                              <BookOpen className="h-3.5 w-3.5" /> Answer Review
                                            </span>
                                            {assessment.submission.auto_marks !== null && assessment.submission.auto_marks !== undefined && (
                                              <span className="text-xs">{assessment.submission.auto_marks}/{assessment.submission.auto_total}</span>
                                            )}
                                          </div>
                                          <div className="divide-y max-h-[300px] overflow-y-auto">
                                            {assessment.questions.map((q, qi) => {
                                              const autoDetail = (assessment.submission.auto_details || []).find(d => d.question === qi + 1);
                                              const studentAnswer = assessment.submission.answers?.[qi];
                                              const studentAnsText = typeof studentAnswer === 'object' ? (studentAnswer?.answer || JSON.stringify(studentAnswer)) : (studentAnswer || '-');
                                              const correctAns = autoDetail?.correct_answer || q.answer || '';
                                              const isCorrect = autoDetail?.is_correct;
                                              return (
                                                <div key={qi} className={`px-3 py-2 text-xs ${isCorrect === true ? 'bg-emerald-50' : isCorrect === false ? 'bg-red-50' : 'bg-gray-50'}`}>
                                                  <p className="font-medium text-gray-900 mb-1">
                                                    {isCorrect === true ? <CheckCircle className="h-3.5 w-3.5 text-emerald-600 inline mr-1" /> : isCorrect === false ? <AlertCircle className="h-3.5 w-3.5 text-red-500 inline mr-1" /> : null}
                                                    Q{qi + 1}. {q.question || q.text || ''}
                                                  </p>
                                                  <p className="text-gray-600"><span className="font-medium">Your answer:</span> <span className={isCorrect === false ? 'text-red-600 line-through' : ''}>{studentAnsText}</span></p>
                                                  {correctAns && <p className="text-emerald-700"><span className="font-medium">Correct answer:</span> {correctAns}</p>}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Tests & Exams Tab */}
          <TabsContent value="tests-exams">
            <Card className="border-0 shadow-md" data-testid="tests-exams-content">
              <CardHeader className="bg-gradient-to-r from-red-50 to-amber-50 border-b">
                <CardTitle className="text-red-800 flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Tests & Exams
                </CardTitle>
                <CardDescription>
                  Timed tests and exams. Once you start, the portal will be locked until you submit.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Practise first — Quick Check banner (any subject with quick check sets) */}
                {quickCheckCount > 0 && (
                  <div className="mb-6 border-2 border-sky-300 rounded-xl overflow-hidden bg-gradient-to-br from-sky-50 to-indigo-50" data-testid="quick-check-banner-tests">
                    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-sky-600" />
                          Practise before you take a test!
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {quickCheckCount} Quick Check practice sets — answer, reveal, self-rate and earn badges. Your teacher can see who practised.
                        </p>
                      </div>
                      <Button
                        onClick={() => setQuickCheckOpen(true)}
                        className="bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-700 hover:to-indigo-700 text-white shadow-md"
                        data-testid="open-quick-check-tests-btn"
                      >
                        <Sparkles className="h-4 w-4 mr-2 text-yellow-300" />
                        Practise Now
                      </Button>
                    </div>
                  </div>
                )}
                <StudentActivities subject={selectedSubject?.subject} token={localStorage.getItem('student_token')} />
                {/* Interactive Assessments — topic tests, class tests & practice exams */}
                <InteractiveExamList
                  subject={selectedSubject?.subject}
                  yearNum={parseInt(student?.year || student?.stream?.match(/Year\s+(\d+)/)?.[1] || student?.year_level?.replace?.(/\D/g, '') || '0')}
                  token={localStorage.getItem('student_token')}
                  onOpen={(id) => { setActiveExamId(id); setExamOpen(true); }}
                  refreshFlag={examOpen}
                  onState={setIexState}
                />

                {sharedLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-red-600" />
                  </div>
                ) : (() => {
                  const subjectFilter = selectedSubject?.subject?.toLowerCase();
                  const testAssessments = sharedAssessments.filter(a => a.lockdown_mode && (!subjectFilter || (a.subject || '').toLowerCase() === subjectFilter));
                  if (testAssessments.length === 0) {
                    if (iexState.loading || iexState.count > 0) return null;
                    return (
                      <div className="text-center py-12 text-gray-500">
                        <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium mb-2">No Tests Available</p>
                        <p className="text-sm">Tests and exams shared by your teachers will appear here.</p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-4">
                      {/* Warning banner */}
                      <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3" data-testid="test-warning-banner">
                        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-amber-800 text-sm">Important: Read before starting</p>
                          <ul className="text-xs text-amber-700 mt-1 space-y-0.5">
                            <li>- Once you click "Start Test", the portal locks and the timer begins</li>
                            <li>- You cannot pause or leave — switching tabs will count as a warning</li>
                            <li>- After warnings, your test will be auto-submitted</li>
                            <li>- Make sure you are ready before starting</li>
                          </ul>
                        </div>
                      </div>

                      {/* Test cards */}
                      {testAssessments.map(assessment => (
                        <div key={assessment.id} className="border-2 border-red-200 rounded-xl overflow-hidden bg-white" data-testid={`test-card-${assessment.id}`}>
                          <div className="bg-red-50 px-4 py-2 flex items-center gap-2 border-b border-red-200">
                            <Lock className="w-4 h-4 text-red-600" />
                            <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">Locked Exam</span>
                            {assessment.time_limit && (
                              <Badge className="bg-red-100 text-red-700 ml-auto text-xs">
                                <Clock className="w-3 h-3 mr-1" /> {assessment.time_limit} minutes
                              </Badge>
                            )}
                          </div>
                          <div className="p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-bold text-gray-900">{assessment.title}</h4>
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  <Badge variant="outline" className="text-xs">{assessment.subject}</Badge>
                                  <Badge variant="outline" className="text-xs">Year {assessment.year_level}</Badge>
                                  <Badge variant="outline" className="text-xs capitalize">{(assessment.assessment_type || '').replace('_', ' ')}</Badge>
                                  <Badge variant="outline" className="text-xs">{assessment.num_questions} Questions</Badge>
                                  {assessment.total_marks && <Badge variant="outline" className="text-xs">{assessment.total_marks} Marks</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">By {assessment.teacher_name} | {assessment.shared_at ? new Date(assessment.shared_at).toLocaleDateString() : ''}</p>
                                {assessment.instructions && (
                                  <p className="text-xs text-gray-600 mt-2 bg-gray-50 p-2 rounded">{assessment.instructions}</p>
                                )}
                              </div>
                              <div className="ml-4 shrink-0">
                                {assessment.submission ? (
                                  assessment.submission.status === 'graded' ? (
                                    <div className="text-right">
                                      <Badge className="bg-green-100 text-green-700 text-base px-3 py-1">{assessment.submission.marks}/{assessment.submission.total_marks || '?'}</Badge>
                                      <p className="text-xs text-green-600 mt-1">Graded</p>
                                    </div>
                                  ) : assessment.submission.status === 'submitted' ? (
                                    <Badge className="bg-yellow-100 text-yellow-700">Submitted</Badge>
                                  ) : (
                                    <Button
                                      size="sm"
                                      className="bg-blue-600 hover:bg-blue-700 text-white"
                                      data-testid={`resume-test-${assessment.id}`}
                                      onClick={() => setExamModeAssessment(assessment)}
                                    >
                                      <Clock className="h-4 w-4 mr-2" /> Resume Test
                                    </Button>
                                  )
                                ) : (
                                  <Button
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                    data-testid={`start-test-${assessment.id}`}
                                    onClick={() => setExamModeAssessment(assessment)}
                                  >
                                    <Shield className="h-4 w-4 mr-2" /> Start Test
                                  </Button>
                                )}
                              </div>
                            </div>
                            {assessment.submission?.status === 'graded' && assessment.submission.feedback && (
                              <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-2">
                                <p className="text-xs font-medium text-green-800">Teacher Feedback:</p>
                                <p className="text-xs text-green-700 mt-0.5">{assessment.submission.feedback}</p>
                              </div>
                            )}
                            {/* Answer Review - shown only after teacher grades */}
                            {assessment.submission?.status === 'graded' && assessment.questions && (
                              <div className="mt-3 border rounded-lg overflow-hidden" data-testid={`answer-review-${assessment.id}`}>
                                <div className="bg-indigo-600 text-white px-4 py-2 flex items-center justify-between">
                                  <span className="text-sm font-semibold flex items-center gap-2">
                                    <BookOpen className="h-4 w-4" /> Answer Review
                                  </span>
                                  {assessment.submission.auto_marks !== null && assessment.submission.auto_marks !== undefined && (
                                    <span className="text-sm">
                                      {assessment.submission.auto_marks}/{assessment.submission.auto_total} auto-graded
                                    </span>
                                  )}
                                </div>
                                <div className="divide-y max-h-[400px] overflow-y-auto">
                                  {assessment.questions.map((q, qi) => {
                                    const autoDetail = (assessment.submission.auto_details || []).find(d => d.question === qi + 1);
                                    const studentAnswer = assessment.submission.answers?.[qi];
                                    const studentAnsText = typeof studentAnswer === 'object' 
                                      ? (studentAnswer?.answer || JSON.stringify(studentAnswer)) 
                                      : (studentAnswer || '-');
                                    const correctAns = autoDetail?.correct_answer || q.answer || '';
                                    const isCorrect = autoDetail?.is_correct;
                                    const isAutoGraded = autoDetail?.auto_graded;
                                    
                                    return (
                                      <div key={qi} className={`px-4 py-3 text-sm ${
                                        isCorrect === true ? 'bg-emerald-50' : 
                                        isCorrect === false ? 'bg-red-50' : 'bg-gray-50'
                                      }`}>
                                        <div className="flex items-start gap-2 mb-1.5">
                                          {isCorrect === true ? (
                                            <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                          ) : isCorrect === false ? (
                                            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                          ) : (
                                            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                          )}
                                          <div className="flex-1">
                                            <p className="font-medium text-gray-900">Q{qi + 1}. {q.question || q.text || ''}</p>
                                            <div className="mt-1 space-y-1">
                                              <p className="text-gray-600">
                                                <span className="font-medium">Your answer:</span>{' '}
                                                <span className={isCorrect === false ? 'text-red-600 line-through' : 'text-gray-700'}>{studentAnsText}</span>
                                              </p>
                                              {correctAns && (
                                                <p className="text-emerald-700">
                                                  <span className="font-medium">Correct answer:</span> {correctAns}
                                                </p>
                                              )}
                                              {!isAutoGraded && isCorrect === undefined && (
                                                <p className="text-xs text-gray-500 italic">Manually reviewed by teacher</p>
                                              )}
                                            </div>
                                          </div>
                                          {autoDetail && (
                                            <Badge className={`flex-shrink-0 ${
                                              isCorrect ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                              {autoDetail.marks_awarded}/{autoDetail.marks_possible}
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {assessment.submission?.auto_submitted && (
                              <div className="mt-2">
                                <Badge className="bg-red-100 text-red-700 text-xs">Auto-submitted: {assessment.submission.auto_submit_reason}</Badge>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* IA Program Tab */}
          <TabsContent value="ia-program">
            <Card className="border-0 shadow-md" data-testid="ia-program-content">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b">
                <CardTitle className="text-emerald-800 flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  Internal Assessment Program
                </CardTitle>
                <CardDescription>
                  Your IA tasks, drafts, and due dates. Submit your work for each draft before the deadline.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {iaLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                  </div>
                ) : iaPrograms.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No IA Program found</p>
                    <p className="text-sm">Your Internal Assessment program will appear here when available.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {iaPrograms.map(iaProgram => (
                    <div key={iaProgram.id || iaProgram.title} className="space-y-4">
                    {/* IA Overview */}
                    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-5 text-white">
                      <h2 className="text-xl font-bold">{iaProgram.title}</h2>
                      <p className="text-emerald-100 mt-1">Total IA Weighting: {iaProgram.total_weighting}% of your course mark</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {iaProgram.tasks.map(task => {
                          const allDraftsSubmitted = task.drafts.every(d => d.submission && ['submitted','reviewed','approved'].includes(d.submission.status));
                          return (
                            <div key={task.id} className={`rounded-lg px-3 py-1.5 text-sm ${allDraftsSubmitted ? 'bg-white/25' : 'bg-white/15'}`}>
                              Task {task.task_number}: {task.title} ({task.weighting}%)
                              {allDraftsSubmitted && <CheckCircle className="inline w-3 h-3 ml-1" />}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Tasks */}
                    {iaProgram.tasks.map(task => {
                      const isExpanded = expandedIATask === task.id;
                      const completedDrafts = task.drafts.filter(d => d.submission && ['submitted','reviewed','approved'].includes(d.submission.status)).length;
                      const totalDrafts = task.drafts.length;
                      const progress = totalDrafts > 0 ? Math.round((completedDrafts / totalDrafts) * 100) : 0;
                      const daysUntilFinal = task.final_submission_date ? Math.ceil((new Date(task.final_submission_date) - new Date()) / (1000*60*60*24)) : null;

                      return (
                        <div key={task.id} className="border rounded-xl overflow-hidden" data-testid={`ia-task-${task.task_number}`}>
                          {/* Task Header */}
                          <div className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => setExpandedIATask(isExpanded ? null : task.id)}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-lg">
                                  {task.task_number}
                                </div>
                                <div>
                                  <h3 className="font-bold text-gray-800">Task {task.task_number}: {task.title}</h3>
                                  <p className="text-sm text-gray-500">{task.theme} | {task.weighting}% weighting</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant={progress === 100 ? "default" : "outline"} className={progress === 100 ? "bg-emerald-600" : ""}>
                                  {completedDrafts}/{totalDrafts} drafts
                                </Badge>
                                {daysUntilFinal !== null && (
                                  <Badge variant={daysUntilFinal < 0 ? "destructive" : daysUntilFinal < 14 ? "warning" : "secondary"}>
                                    {daysUntilFinal < 0 ? 'Overdue' : `${daysUntilFinal}d left`}
                                  </Badge>
                                )}
                                {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                              </div>
                            </div>
                            {/* Progress Bar */}
                            <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                            </div>
                          </div>

                          {/* Expanded Task Content */}
                          {isExpanded && (
                            <div className="p-4 space-y-4 border-t">
                              <p className="text-sm text-gray-600">{task.description}</p>

                              {/* Key Dates */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                                  <div className="text-xs text-blue-600 font-medium">Start</div>
                                  <div className="text-sm font-semibold">{task.start_date ? new Date(task.start_date).toLocaleDateString('en-GB', {day:'numeric',month:'short'}) : '-'}</div>
                                </div>
                                <div className="bg-orange-50 rounded-lg p-2.5 text-center">
                                  <div className="text-xs text-orange-600 font-medium">End</div>
                                  <div className="text-sm font-semibold">{task.end_date ? new Date(task.end_date).toLocaleDateString('en-GB', {day:'numeric',month:'short'}) : '-'}</div>
                                </div>
                                <div className="bg-red-50 rounded-lg p-2.5 text-center">
                                  <div className="text-xs text-red-600 font-medium">Final Due</div>
                                  <div className="text-sm font-semibold">{task.final_submission_date ? new Date(task.final_submission_date).toLocaleDateString('en-GB', {day:'numeric',month:'short'}) : '-'}</div>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-2.5 text-center">
                                  <div className="text-xs text-purple-600 font-medium">Results to EAU</div>
                                  <div className="text-sm font-semibold">{task.results_to_eau_date ? new Date(task.results_to_eau_date).toLocaleDateString('en-GB', {day:'numeric',month:'short'}) : '-'}</div>
                                </div>
                              </div>

                              {/* Draft Timeline */}
                              {task.drafts?.length > 0 && (
                                <div className="border border-blue-200 rounded-lg overflow-hidden" data-testid={`draft-timeline-${task.task_number}`}>
                                  <div className="bg-blue-700 px-4 py-2.5">
                                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                                      <Calendar className="w-4 h-4" />
                                      Draft Timeline - Task {task.task_number}
                                    </h4>
                                  </div>
                                  <div className="p-4">
                                    <div className="relative">
                                      {/* Timeline line */}
                                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-blue-200" />
                                      <div className="space-y-4">
                                        {task.drafts.map((draft, dIdx) => {
                                          const sub = draft.submission;
                                          const isSubmitted = sub && ['submitted','reviewed','approved'].includes(sub.status);
                                          const daysLeft = draft.end_date ? Math.ceil((new Date(draft.end_date) - new Date()) / (1000*60*60*24)) : null;
                                          const isOverdue = daysLeft !== null && daysLeft < 0 && !isSubmitted;
                                          const isCurrent = daysLeft !== null && daysLeft >= 0 && !isSubmitted && 
                                            (dIdx === 0 || task.drafts[dIdx-1]?.submission);

                                          return (
                                            <div key={draft.id} className="relative pl-10" data-testid={`timeline-draft-${task.task_number}-${draft.draft_number}`}>
                                              {/* Timeline dot */}
                                              <div className={`absolute left-2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                                isSubmitted ? 'bg-emerald-500 border-emerald-500' :
                                                isOverdue ? 'bg-red-500 border-red-500' :
                                                isCurrent ? 'bg-blue-500 border-blue-500 ring-4 ring-blue-100' :
                                                'bg-white border-gray-300'
                                              }`}>
                                                {isSubmitted && <CheckCircle className="w-3 h-3 text-white" />}
                                                {isOverdue && <AlertTriangle className="w-3 h-3 text-white" />}
                                              </div>
                                              {/* Draft info */}
                                              <div className={`rounded-lg p-3 ${
                                                isSubmitted ? 'bg-emerald-50 border border-emerald-200' :
                                                isOverdue ? 'bg-red-50 border border-red-200' :
                                                isCurrent ? 'bg-blue-50 border-2 border-blue-300' :
                                                'bg-gray-50 border border-gray-200'
                                              }`}>
                                                <div className="flex items-center justify-between">
                                                  <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-sm">Draft {draft.draft_number}</span>
                                                    {isSubmitted && <Badge className="bg-emerald-600 text-white text-xs">Submitted</Badge>}
                                                    {sub?.status === 'reviewed' && <Badge className="bg-blue-600 text-white text-xs">Reviewed</Badge>}
                                                    {sub?.status === 'approved' && <Badge className="bg-emerald-600 text-white text-xs">Approved</Badge>}
                                                    {isOverdue && <Badge variant="destructive" className="text-xs">Overdue</Badge>}
                                                    {isCurrent && <Badge className="bg-blue-600 text-white text-xs animate-pulse">Current</Badge>}
                                                  </div>
                                                  <div className="text-xs text-gray-500">
                                                    {draft.start_date && new Date(draft.start_date).toLocaleDateString('en-GB', {day:'numeric',month:'short'})}
                                                    {' - '}
                                                    {draft.end_date && new Date(draft.end_date).toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'2-digit'})}
                                                  </div>
                                                </div>
                                                {isCurrent && daysLeft !== null && (
                                                  <div className="mt-1.5 flex items-center gap-2">
                                                    <div className="flex-1 bg-blue-200 rounded-full h-1.5">
                                                      <div className="bg-blue-600 h-1.5 rounded-full" style={{
                                                        width: `${Math.min(100, Math.max(5, draft.start_date ? 
                                                          ((new Date() - new Date(draft.start_date)) / (new Date(draft.end_date) - new Date(draft.start_date))) * 100 : 50))}%`
                                                      }} />
                                                    </div>
                                                    <span className="text-xs font-medium text-blue-700">{daysLeft}d left</span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                        {/* Final submission marker */}
                                        <div className="relative pl-10">
                                          <div className="absolute left-2 w-5 h-5 rounded-full border-2 bg-purple-500 border-purple-500 flex items-center justify-center">
                                            <Flag className="w-3 h-3 text-white" />
                                          </div>
                                          <div className="rounded-lg p-3 bg-purple-50 border border-purple-200">
                                            <div className="flex items-center justify-between">
                                              <span className="font-semibold text-sm text-purple-800">Final Submission</span>
                                              <span className="text-xs text-purple-600">
                                                {task.final_submission_date && new Date(task.final_submission_date).toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'2-digit'})}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* What you need to do */}
                              {task.task_outline && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                  <h4 className="text-sm font-semibold text-amber-800 mb-1">What You Need To Do:</h4>
                                  <div className="text-sm text-amber-700 whitespace-pre-line">{task.task_outline}</div>
                                </div>
                              )}

                              {/* Marking Rubric */}
                              {task.learning_outcomes?.length > 0 && (
                                <div className="border border-emerald-200 rounded-lg overflow-hidden" data-testid={`ia-rubric-${task.task_number}`}>
                                  <div className="bg-emerald-700 px-4 py-2.5">
                                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                                      <ClipboardList className="w-4 h-4" />
                                      Marking Rubric - Task {task.task_number}
                                    </h4>
                                    <p className="text-emerald-200 text-xs mt-0.5">Your work will be assessed using this rubric. Review it carefully before submitting each draft.</p>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm" data-testid={`rubric-table-${task.task_number}`}>
                                      <thead>
                                        <tr className="bg-emerald-50 border-b border-emerald-200">
                                          <th className="text-left px-3 py-2 text-xs font-semibold text-emerald-700 w-8">#</th>
                                          <th className="text-left px-3 py-2 text-xs font-semibold text-emerald-700">Learning Outcome</th>
                                          <th className="text-left px-3 py-2 text-xs font-semibold text-emerald-700 w-24">SLO Code</th>
                                          <th className="text-center px-3 py-2 text-xs font-semibold text-emerald-700 w-16">Level</th>
                                          <th className="text-left px-3 py-2 text-xs font-semibold text-emerald-700">Criteria</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {task.learning_outcomes.map((lo, loIdx) => {
                                          const skillLevel = lo.skill_level || 1;
                                          const levels = [];
                                          for (let lvl = skillLevel; lvl >= 0; lvl--) {
                                            let criteria = '';
                                            if (lvl === skillLevel) criteria = `${lo.description} completed correctly and to the required standard`;
                                            else if (lvl === skillLevel - 1 && skillLevel >= 2) criteria = 'Some components of the task were completed using appropriate methods';
                                            else if (lvl === 1) criteria = 'A basic component or single element was presented';
                                            else if (lvl === 0) criteria = 'An attempt was made';
                                            else criteria = `Part of the required work was completed at level ${lvl}`;
                                            levels.push({ level: lvl, criteria });
                                          }
                                          return levels.map((item, lvlIdx) => (
                                            <tr key={`${loIdx}-${item.level}`}
                                              className={`border-b border-gray-100 ${lvlIdx === 0 ? 'border-t-2 border-t-emerald-200' : ''} ${loIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                              {lvlIdx === 0 && (
                                                <>
                                                  <td className="px-3 py-2 text-xs font-bold text-gray-600 align-top" rowSpan={levels.length}>{loIdx + 1}</td>
                                                  <td className="px-3 py-2 text-xs text-gray-700 align-top" rowSpan={levels.length}>{lo.description}</td>
                                                  <td className="px-3 py-2 text-xs font-mono text-emerald-600 align-top" rowSpan={levels.length}>{lo.slo_code}</td>
                                                </>
                                              )}
                                              <td className="px-3 py-2 text-center">
                                                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                                  item.level === skillLevel ? 'bg-emerald-100 text-emerald-700' :
                                                  item.level === 0 ? 'bg-gray-100 text-gray-500' :
                                                  'bg-blue-50 text-blue-600'
                                                }`}>{item.level}</span>
                                              </td>
                                              <td className="px-3 py-2 text-xs text-gray-600">{item.criteria}</td>
                                            </tr>
                                          ));
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* Drafts */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                                  <FileText className="w-4 h-4" /> Your Drafts
                                </h4>
                                {task.drafts.map(draft => {
                                  const sub = draft.submission;
                                  const daysLeft = draft.end_date ? Math.ceil((new Date(draft.end_date) - new Date()) / (1000*60*60*24)) : null;
                                  const statusColor = sub?.status === 'approved' ? 'border-emerald-400 bg-emerald-50' :
                                    sub?.status === 'reviewed' ? 'border-blue-400 bg-blue-50' :
                                    sub?.status === 'submitted' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200';
                                  
                                  return (
                                    <div key={draft.id} className={`border-2 rounded-lg p-4 ${statusColor}`}
                                      data-testid={`ia-draft-${task.task_number}-${draft.draft_number}`}>
                                      <div className="flex items-start justify-between mb-2">
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="text-xs font-semibold">Draft {draft.draft_number}</Badge>
                                            {sub?.status === 'approved' && <Badge className="bg-emerald-600 text-xs">Approved</Badge>}
                                            {sub?.status === 'reviewed' && <Badge className="bg-blue-600 text-xs">Reviewed</Badge>}
                                            {sub?.status === 'submitted' && <Badge className="bg-yellow-600 text-xs">Submitted</Badge>}
                                          </div>
                                          <h5 className="font-medium text-sm mt-1">{draft.title}</h5>
                                        </div>
                                        <div className="text-right">
                                          {daysLeft !== null && (
                                            <span className={`text-xs font-medium ${daysLeft < 0 ? 'text-red-600' : daysLeft < 7 ? 'text-orange-600' : 'text-gray-500'}`}>
                                              {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d left`}
                                            </span>
                                          )}
                                          <div className="text-xs text-gray-400">
                                            {draft.start_date && new Date(draft.start_date).toLocaleDateString('en-GB', {day:'numeric',month:'short'})}
                                            {' - '}
                                            {draft.end_date && new Date(draft.end_date).toLocaleDateString('en-GB', {day:'numeric',month:'short'})}
                                          </div>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">{draft.description}</p>

                                      {/* Teacher Feedback */}
                                      {sub?.teacher_feedback && (
                                        <div className="bg-white border rounded-lg p-3 mb-3">
                                          <div className="text-xs font-semibold text-blue-600 mb-1">Teacher Feedback:</div>
                                          <p className="text-sm">{sub.teacher_feedback}</p>
                                          {sub.score !== null && (
                                            <Badge variant="secondary" className="mt-1">Score: {sub.score}</Badge>
                                          )}
                                        </div>
                                      )}

                                      {/* Submit area - only if not yet approved */}
                                      {sub?.status !== 'approved' && (
                                        <div className="space-y-2">
                                          {/* Uploaded files display */}
                                          {(sub?.files?.length > 0 || iaUploadedFiles[draft.id]?.length > 0) && (
                                            <div className="bg-gray-50 rounded-lg p-2">
                                              <div className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                                <Paperclip className="w-3 h-3" /> Attached Files:
                                              </div>
                                              <div className="flex flex-wrap gap-1.5">
                                                {[...(sub?.files || []), ...(iaUploadedFiles[draft.id] || [])].filter((f, i, a) => a.indexOf(f) === i).map(fname => {
                                                  const isImage = /\.(png|jpg|jpeg|gif)$/i.test(fname);
                                                  return (
                                                    <a key={fname}
                                                      href={`${API_URL}/api/ia-programs/files/${fname}`}
                                                      target="_blank" rel="noopener noreferrer"
                                                      className="inline-flex items-center gap-1 bg-white border rounded px-2 py-1 text-xs hover:bg-blue-50 transition-colors"
                                                      data-testid={`draft-file-${fname}`}>
                                                      {isImage ? <Image className="w-3 h-3 text-blue-500" /> : <FileText className="w-3 h-3 text-gray-500" />}
                                                      <span className="max-w-[120px] truncate">{fname.split('_').pop()}</span>
                                                      <Download className="w-3 h-3 text-gray-400" />
                                                    </a>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          )}
                                          <Textarea
                                            placeholder="Write your work here... Describe what you did, include your findings, evidence, and results."
                                            className="text-sm min-h-[100px]"
                                            value={iaDraftContent[draft.id] || ''}
                                            onChange={e => setIADraftContent(prev => ({ ...prev, [draft.id]: e.target.value }))}
                                            onPaste={e => handlePaste(`ia-draft-${draft.id}`, e)}
                                            data-testid={`ia-draft-input-${draft.draft_number}`}
                                          />
                                          <div className="flex items-center justify-between">
                                            <Button
                                              size="sm" variant="outline"
                                              disabled={iaUploading[draft.id]}
                                              onClick={() => uploadIAFile(draft.id, 'draft')}
                                              data-testid={`ia-upload-draft-${draft.draft_number}`}
                                            >
                                              {iaUploading[draft.id] ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                              ) : (
                                                <Upload className="w-4 h-4 mr-1" />
                                              )}
                                              Attach File
                                            </Button>
                                            <Button
                                              size="sm"
                                              className="bg-emerald-600 hover:bg-emerald-700"
                                              disabled={iaSubmitting[draft.id] || !iaDraftContent[draft.id]?.trim()}
                                              onClick={() => submitIADraft(draft.id)}
                                              data-testid={`ia-submit-draft-${draft.draft_number}`}
                                            >
                                              {iaSubmitting[draft.id] ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                              ) : (
                                                <Send className="w-4 h-4 mr-1" />
                                              )}
                                              {sub?.status === 'submitted' || sub?.status === 'reviewed' ? 'Resubmit Draft' : 'Submit Draft'}
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Final Submission */}
                              <div className="border-2 border-dashed border-emerald-300 rounded-lg p-4 bg-emerald-50/50">
                                <h4 className="font-semibold text-emerald-800 flex items-center gap-2 mb-2">
                                  <CheckCircle className="w-4 h-4" /> Final Submission
                                </h4>
                                <p className="text-sm text-gray-600 mb-3">
                                  After all drafts are reviewed, compile everything into your final document with evidence (screenshots).
                                </p>
                                {task.final_submission?.status === 'marked' ? (
                                  <div className="bg-white rounded-lg p-3 border">
                                    <Badge className="bg-emerald-600 mb-2">Marked</Badge>
                                    <p className="text-lg font-bold text-emerald-700">
                                      {task.final_submission.marks}/{task.final_submission.total_marks} marks
                                    </p>
                                    {task.final_submission.teacher_feedback && (
                                      <p className="text-sm text-gray-600 mt-1">{task.final_submission.teacher_feedback}</p>
                                    )}
                                  </div>
                                ) : task.final_submission?.status === 'submitted' ? (
                                  <div className="bg-white rounded-lg p-3 border">
                                    <Badge className="bg-yellow-600 mb-2">Submitted - Awaiting Marking</Badge>
                                    <p className="text-sm text-gray-500">Your final document has been submitted. Teacher will review and mark it.</p>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {/* Uploaded final files */}
                                    {(task.final_submission?.files?.length > 0 || iaUploadedFiles[task.id]?.length > 0) && (
                                      <div className="bg-white rounded-lg p-2 border">
                                        <div className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                          <Paperclip className="w-3 h-3" /> Attached Evidence:
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                          {[...(task.final_submission?.files || []), ...(iaUploadedFiles[task.id] || [])].filter((f, i, a) => a.indexOf(f) === i).map(fname => {
                                            const isImage = /\.(png|jpg|jpeg|gif)$/i.test(fname);
                                            return (
                                              <a key={fname}
                                                href={`${API_URL}/api/ia-programs/files/${fname}`}
                                                target="_blank" rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 bg-gray-50 border rounded px-2 py-1 text-xs hover:bg-blue-50 transition-colors">
                                                {isImage ? <Image className="w-3 h-3 text-blue-500" /> : <FileText className="w-3 h-3 text-gray-500" />}
                                                <span className="max-w-[120px] truncate">{fname.split('_').pop()}</span>
                                                <Download className="w-3 h-3 text-gray-400" />
                                              </a>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                    <Textarea
                                      placeholder="Compile your final work here... Include all evidence and screenshots references."
                                      className="text-sm min-h-[120px]"
                                      value={iaFinalContent[task.id] || ''}
                                      onChange={e => setIAFinalContent(prev => ({ ...prev, [task.id]: e.target.value }))}
                                      onPaste={e => handlePaste(`ia-final-${task.id}`, e)}
                                      data-testid={`ia-final-input-${task.task_number}`}
                                    />
                                    <div className="flex items-center justify-between">
                                      <Button
                                        size="sm" variant="outline"
                                        disabled={iaUploading[task.id]}
                                        onClick={() => uploadIAFile(task.id, 'final')}
                                        data-testid={`ia-upload-final-${task.task_number}`}
                                      >
                                        {iaUploading[task.id] ? (
                                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                        ) : (
                                          <Upload className="w-4 h-4 mr-1" />
                                        )}
                                        Attach Evidence
                                      </Button>
                                      <Button
                                        className="bg-emerald-700 hover:bg-emerald-800"
                                        disabled={iaSubmitting[task.id] || !iaFinalContent[task.id]?.trim()}
                                        onClick={() => submitIAFinal(task.id)}
                                        data-testid={`ia-submit-final-${task.task_number}`}
                                      >
                                        {iaSubmitting[task.id] ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                                        Submit Final Document
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Learning Outcomes */}
                              <details className="group">
                                <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-emerald-700">
                                  View Learning Outcomes ({task.learning_outcomes.length})
                                </summary>
                                <div className="mt-2 space-y-1.5">
                                  {task.learning_outcomes.map((lo, idx) => (
                                    <div key={idx} className="flex items-start gap-2 bg-gray-50 rounded p-2 text-sm">
                                      <Badge variant="secondary" className="text-xs flex-shrink-0">L{lo.skill_level}</Badge>
                                      <div>
                                        <code className="text-xs text-emerald-600">{lo.slo_code}</code>
                                        <p className="text-xs mt-0.5">{lo.description}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments">
            <Card className="border-0 shadow-md" data-testid="assignments-content">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-amber-50 border-b">
                <CardTitle className="text-emerald-800 flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  Your Assignments
                </CardTitle>
                <CardDescription>
                  Track your assignments, projects, and exercises
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                  </div>
                ) : assignments && assignments.length > 0 ? (
                  <div className="space-y-3">
                    {assignments.map((assignment, index) => (
                      <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`assignment-${index}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {getStatusIcon(assignment.status)}
                              <h3 className="font-semibold text-gray-800">{assignment.title}</h3>
                            </div>
                            <p className="text-sm text-gray-500">{assignment.subject} - {assignment.class_name}</p>
                            {assignment.due_date && (
                              <p className="text-xs text-gray-400 mt-1">
                                Due: {new Date(assignment.due_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {getStatusBadge(assignment.status)}
                            {assignment.marks_obtained !== null && (
                              <span className="text-sm font-medium text-emerald-600">
                                {assignment.marks_obtained}/{assignment.max_marks}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No assignments found</p>
                    <p className="text-sm">Your assignments and projects will appear here.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
          </div>
        )}
      </div>

      {/* Quick Check Modal */}
      <QuickCheckModal
        open={quickCheckOpen}
        onClose={() => setQuickCheckOpen(false)}
        subject={selectedSubject?.subject || 'ICT'}
        yearLevel={parseInt(student?.year || student?.stream?.match(/Year\s+(\d+)/)?.[1] || student?.year_level?.replace?.(/\D/g, '') || '12')}
        token={localStorage.getItem('student_token')}
      />

      {/* Interactive Exam Modal */}
      <InteractiveExamModal
        open={examOpen}
        onClose={() => setExamOpen(false)}
        examId={activeExamId}
        subject={selectedSubject?.subject || 'ICT'}
        yearLevel={parseInt(student?.year || student?.stream?.match(/Year\s+(\d+)/)?.[1] || student?.year_level?.replace?.(/\D/g, '') || '12')}
        token={localStorage.getItem('student_token')}
      />
    </div>
    </>
  );
}
